/*
 * Copyright (c) 2024, King Living
 * Custom Hooks for Google Maps Integration
 * GM-008: Geolocation on page load
 * GM-010: "Use my location" functionality
 *
 * Uses Google Geocoding API for address-to-coordinates conversion
 * Uses SFCC Shopper Stores API for store search
 */

import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {useSearchStores} from '@salesforce/commerce-sdk-react';

import {
  DEFAULT_STORE_SEARCH_RADIUS,
  FALLBACK_STORE_SEARCH_RADIUS,
  DEFAULT_STORE_LIMIT,
  FALLBACK_STORE_LIMIT,
} from '../constants/site-config';
import {useGeocoding} from './use-geocoding';

/**
 * Geolocation Hook
 * Handles browser geolocation API for getting user's current position
 */
export function useGeolocation() {
  const [position, setPosition] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const getCurrentPosition = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        setError('Geolocation is not supported by this browser');

        return reject(new Error('Geolocation not supported'));
      }

      setIsLoading(true);
      setError(null);

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          };

          setPosition(coords);
          setIsLoading(false);
          resolve(coords);
        },
        (geoError) => {
          let errorMessage = 'Unable to retrieve your location';

          switch (geoError.code) {
            case geoError.PERMISSION_DENIED:
              errorMessage = 'Location access denied by user';
              break;
            case geoError.POSITION_UNAVAILABLE:
              errorMessage = 'Location information is unavailable';
              break;
            case geoError.TIMEOUT:
              errorMessage = 'Location request timed out';
              break;
            default:
              errorMessage = 'An unknown error occurred';
              break;
          }

          setError(errorMessage);
          setIsLoading(false);
          reject(new Error(errorMessage));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000, // 5 minutes
        }
      );
    });
  }, []);

  return {
    position,
    isLoading,
    error,
    getCurrentPosition,
  };
}

/**
 * Custom marker icon SVG with "K" character for King Living
 * Black square shape with arrow tail and white "K" letter
 */
const KING_MARKER_ICON_SVG = `
<svg xmlns="http://www.w3.org/2000/svg" width="32" height="38" viewBox="0 0 32 38">
  <rect x="0" y="0" width="32" height="32" fill="#000000"/>
  <polygon points="12,32 20,32 16,38" fill="#000000"/>
  <text x="16" y="21" font-family="Arial, sans-serif" font-size="16" font-weight="bold" fill="#FFFFFF" text-anchor="middle">K</text>
</svg>
`;

/**
 * Get the custom marker icon as a data URL
 * @returns {Object} Custom marker icon object for Google Maps
 */
export const getCustomMarkerIcon = () => {
  const encodedSvg = encodeURIComponent(KING_MARKER_ICON_SVG.trim());

  return {
    url: `data:image/svg+xml,${encodedSvg}`,
    scaledSize: {width: 32, height: 38},
    anchor: {x: 16, y: 38}, // Tip of the arrow tail
  };
};

/**
 * Create store markers from SFCC store data
 * @param {Array} stores - Array of stores from SFCC API
 * @returns {Array} Array of marker objects for GoogleMap component
 */
const createStoreMarkers = (stores) => {
  if (!stores || stores.length === 0) return [];

  const customIcon = getCustomMarkerIcon();

  return stores.map((store) => {
    return {
      position: {
        lat: store.latitude,
        lng: store.longitude,
      },
      title: store.name,
      icon: customIcon,
    };
  });
};

/**
 * Main Google Maps Integration Hook
 * Combines geocoding, geolocation and store search functionality
 *
 * @param {Object} options - Configuration options
 * @param {string} options.countryCode - Country code for geocoding bias (e.g., 'AU', 'US')
 * @param {string} options.locale - Locale for API requests (e.g., 'en-AU')
 * @param {string} options.distanceUnit - Distance unit for store search ('km' or 'miles')
 * @param {number} options.searchRadius - Search radius from site preference (default: 1000km)
 * @returns {Object} Integration state and methods
 */
export function useGoogleMapsIntegration({
  countryCode = 'AU',
  locale = 'en-AU',
  distanceUnit = 'km',
  searchRadius = DEFAULT_STORE_SEARCH_RADIUS,
} = {}) {
  // Map state
  const [mapCenter, setMapCenter] = useState(null);
  const [markers, setMarkers] = useState([]);

  // Track if we need to expand search (no results in initial radius)
  const [isFallbackResults, setIsFallbackResults] = useState(false);
  const lastSearchCoordsRef = useRef(null);

  // Search state
  const [searchParams, setSearchParams] = useState({
    latitude: null,
    longitude: null,
    maxDistance: searchRadius,
    distanceUnit,
    limit: DEFAULT_STORE_LIMIT,
    locale,
  });
  const [searchError, setSearchError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Initialize hooks
  const geolocation = useGeolocation();
  const geocoding = useGeocoding();

  // SFCC Store Search API
  const {
    data: storesData,
    isLoading: isStoresLoading,
    error: storesApiError,
    refetch: refetchStores,
  } = useSearchStores(
    {
      parameters: {
        latitude: searchParams.latitude,
        longitude: searchParams.longitude,
        maxDistance: searchParams.maxDistance,
        distanceUnit: searchParams.distanceUnit,
        limit: searchParams.limit,
        locale: searchParams.locale,
      },
    },
    {
      // Only fetch when we have coordinates
      enabled: searchParams.latitude !== null && searchParams.longitude !== null,
    }
  );

  // Extract stores from API response and sort by distance (nearest first)
  const stores = useMemo(() => {
    const storeList = storesData?.data || [];

    // Sort stores by distance (ascending - closest first)
    return [...storeList].sort((a, b) => {
      const distanceA = a.distance ?? Infinity;
      const distanceB = b.distance ?? Infinity;

      return distanceA - distanceB;
    });
  }, [storesData?.data]);

  // Update markers when stores change
  const updateMarkersFromStores = useCallback((storeList) => {
    const storeMarkers = createStoreMarkers(storeList);

    setMarkers(storeMarkers);
  }, []);

  /**
   * Search stores by coordinates
   * Updates searchParams to trigger SFCC API call
   *
   * @param {Object} params - Search parameters
   * @param {number} params.latitude - Latitude
   * @param {number} params.longitude - Longitude
   * @param {number} params.maxDistance - Max distance (optional, uses searchRadius)
   * @param {boolean} params.isInitialLoad - Whether this is the initial page load
   * @param {boolean} params.isFallback - Whether this is a fallback search with expanded radius
   */
  const searchByCoordinates = useCallback(async ({
    latitude,
    longitude,
    maxDistance,
    isInitialLoad = false,
    isFallback = false,
  }) => {
    setSearchError(null);

    // Store coordinates for potential fallback search
    lastSearchCoordsRef.current = {latitude, longitude};

    // Track if this is a fallback search (results from expanded radius)
    // Reset to false for new searches, set to true for fallback
    setIsFallbackResults(isFallback);

    // Determine the radius to use
    let effectiveRadius = maxDistance || searchRadius;
    let effectiveLimit = DEFAULT_STORE_LIMIT;

    // For initial load or fallback, use large radius to ensure we get stores
    if (isInitialLoad || isFallback) {
      effectiveRadius = FALLBACK_STORE_SEARCH_RADIUS;
      effectiveLimit = isFallback ? FALLBACK_STORE_LIMIT : DEFAULT_STORE_LIMIT;
    }

    // Update search params to trigger store search
    setSearchParams((prev) => ({
      ...prev,
      latitude,
      longitude,
      maxDistance: effectiveRadius,
      limit: effectiveLimit,
    }));

    // Update map center
    setMapCenter({
      lat: latitude,
      lng: longitude,
    });

    return {latitude, longitude};
  }, [searchRadius]);

  /**
   * GM-010: Handle "Use my location" button click
   * Gets user's current position via browser geolocation API
   * Then searches for stores near that location
   */
  const handleUseMyLocation = useCallback(async () => {
    setSearchError(null);

    // Get user's current position
    const coords = await geolocation.getCurrentPosition();

    // Search stores near user location
    await searchByCoordinates({
      latitude: coords.latitude,
      longitude: coords.longitude,
    });

    return coords;
  }, [geolocation, searchByCoordinates]);

  /**
   * Handle location search by address query
   * Uses Google Geocoding API to convert address to coordinates
   * Then searches for stores near that location
   *
   * @param {string} query - Address/suburb/postcode to search
   */
  const handleLocationSearch = useCallback(async (query) => {
    setSearchError(null);
    setSearchQuery(query);

    try {
      // Use Google Geocoding API with country filter
      const geocodeResult = await geocoding.geocodeWithCountry(query, countryCode);

      // Extract coordinates from first result
      const coordinates = geocoding.extractCoordinates(geocodeResult);

      if (!coordinates) {
        // Format error message for invalid search term
        const error = `NO_RESULTS:${query}`;

        setSearchError(error);
        throw new Error(error);
      }

      // Search stores at geocoded location using configured radius
      await searchByCoordinates({
        latitude: coordinates.lat,
        longitude: coordinates.lng,
        maxDistance: searchRadius,
      });

      return coordinates;
    } catch (error) {
      // Check if it's a configuration error (API key missing)
      const isConfigError = error.message?.includes('503') ||
        error.message?.includes('CONFIGURATION_ERROR') ||
        error.message?.includes('not configured');

      let errorMessage;

      if (isConfigError) {
        errorMessage = 'Geocoding service is not available. Please try again later.';
        console.warn('[Google Maps Integration] Geocoding service not configured:', error.message);
      } else {
        errorMessage = `No results found for "${query}". Please try a different search term.`;
      }

      setSearchError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [countryCode, geocoding, searchByCoordinates, searchRadius]);

  /**
   * GM-008: Load stores on page load using default location
   * Does NOT request user location - uses provided default coordinates
   * Uses large radius to ensure we get at least 20 stores
   */
  const loadStoresOnPageLoad = useCallback(async (defaultLocation) => {
    if (!defaultLocation?.lat || !defaultLocation?.lng) {
      console.warn('No default location provided for initial store load');

      return [];
    }

    await searchByCoordinates({
      latitude: defaultLocation.lat,
      longitude: defaultLocation.lng,
      isInitialLoad: true,
    });

    return stores;
  }, [searchByCoordinates, stores]);

  // Handle fallback search when no stores found within initial radius
  // If a search returns 0 results and it wasn't already a fallback search,
  // automatically expand the search radius to find nearest stores
  useEffect(() => {
    // Only trigger fallback if:
    // 1. We have search coordinates
    // 2. Search is complete (not loading)
    // 3. No stores found
    // 4. Not already an expanded search
    // 5. No API error
    if (
      lastSearchCoordsRef.current &&
      !isStoresLoading &&
      stores.length === 0 &&
      !isFallbackResults &&
      !storesApiError &&
      searchParams.latitude !== null
    ) {
      // Expand search to find nearest stores
      searchByCoordinates({
        latitude: lastSearchCoordsRef.current.latitude,
        longitude: lastSearchCoordsRef.current.longitude,
        isFallback: true,
      });
    }
  }, [stores.length, isStoresLoading, storesApiError, searchParams.latitude, searchByCoordinates, isFallbackResults]);

  // Manual marker update - called after stores are loaded
  const updateMarkers = useCallback(() => {
    if (stores.length > 0) {
      updateMarkersFromStores(stores);
    }
  }, [stores, updateMarkersFromStores]);

  // Memoize final markers to prevent unnecessary re-renders
  const finalMarkers = useMemo(() => {
    if (markers.length > 0) {
      return markers;
    }

    return createStoreMarkers(stores);
  }, [markers, stores]);

  return {
    // Geolocation state
    geolocationError: geolocation.error,
    isGeolocationLoading: geolocation.isLoading,
    getCurrentPosition: geolocation.getCurrentPosition,

    // Geocoding state
    geocodingError: geocoding.error,
    isGeocodingLoading: geocoding.loading,

    // Store search state
    stores,
    searchError: searchError || (storesApiError?.message),
    isSearchLoading: isStoresLoading || geocoding.loading,
    isFallbackResults, // True when showing results from expanded radius search
    searchQuery,

    // Map state
    mapCenter,
    markers: finalMarkers,

    // Actions
    loadStoresOnPageLoad,
    handleUseMyLocation,
    handleLocationSearch,
    searchByCoordinates,
    refetchStores,
    updateMarkers,
  };
}
